import React, { useMemo, useRef, useState } from 'react';
import { Flex } from 'rebass';
import { Textarea } from '@rebass/forms';
import { useMutation } from '@apollo/client';
import { sortHistoryItems } from '../Timeline/helpers';

import Timeline from '../Timeline/Timeline';
import { HistoryItem } from '../Timeline/types';
import {
  convertBackflowAssembly,
  convertServiceAddress,
  convertWaterHazardSurvey,
  convertWaterServiceAttachment,
  convertWaterServiceComment,
} from './helpers';
import { gql } from '@/modes/waterSupplier/gql';
import RequirePerms from '../RequirePerms';
import { WaterService } from './WaterServiceDetail';
import useMode from '@/hooks/useMode';

type WaterServiceHistoryType =
  | 'COMMENT'
  | 'SURVEY'
  | 'ASSEMBLY'
  | 'ATTACHMENT'
  | 'CUSTOMER';

const CREATE_WATER_SERVICE_COMMENT = gql(`
  mutation CreateWaterServiceComment($input: WaterServiceCommentInput!) {
    createWaterServiceComment(input: $input) {
      id
    }
  }
`);

type WaterServiceHistoryProps = {
  waterServiceId: string;
  waterService: WaterService;
  refetch?: () => void;
};

const WaterServiceHistory: React.FC<WaterServiceHistoryProps> = ({
  waterService,
  waterServiceId,
  refetch,
}) => {
  const [createWaterServiceComment] = useMutation(
    CREATE_WATER_SERVICE_COMMENT,
    {}
  );
  const [comment, setComment] = useState('');
  const mode = useMode();

  const scrollDiv = useRef<any>();

  const [selectedHistoryItemTypes, setSelectedHistoryItemTypes] = useState<
    WaterServiceHistoryType[]
  >(['COMMENT', 'SURVEY', 'ASSEMBLY', 'ATTACHMENT', 'CUSTOMER']);

  // Cast to any[] to work around gql.tada scalar inference producing {} for BigInt fields
  const comments = waterService.comments ?? [];
  const surveys = waterService.waterHazardSurveys ?? [];
  const assemblies = waterService.backflowAssemblies ?? [];
  const attachments = waterService.attachments ?? [];
  const serviceAddresses = waterService.serviceAddresses ?? [];

  const availableHistoryItemTypes = useMemo<WaterServiceHistoryType[]>(() => {
    const available: WaterServiceHistoryType[] = [];
    if (comments.length) available.push('COMMENT');
    if (surveys.length) available.push('SURVEY');
    if (assemblies.length) available.push('ASSEMBLY');
    if (attachments.length) available.push('ATTACHMENT');
    if (serviceAddresses.length) available.push('CUSTOMER');
    return available;
  }, [waterService]);

  const amendedHistoryItems = useMemo<
    HistoryItem<WaterServiceHistoryType>[]
  >(() => {
    const items: HistoryItem<WaterServiceHistoryType>[] = [
      ...comments.map(convertWaterServiceComment),
      ...surveys.map((s) => convertWaterHazardSurvey(s, mode)),
      ...assemblies.map((a) => convertBackflowAssembly(a, mode)),
      ...attachments.map(convertWaterServiceAttachment),
      ...serviceAddresses.map((sa) => convertServiceAddress(sa, mode)),
    ];

    return items
      .filter((item) => selectedHistoryItemTypes.includes(item.type!))
      .sort(sortHistoryItems);
  }, [waterService, selectedHistoryItemTypes, mode]);

  return (
    <Flex flexDirection="column" height="100%" flexGrow={1} flexShrink={1}>
      <Timeline
        title="History"
        historyItems={amendedHistoryItems}
        scrollDivRef={scrollDiv}
        historyItemTypes={availableHistoryItemTypes}
        selectedHistoryItemTypes={selectedHistoryItemTypes}
        setSelectedHistoryItemTypes={setSelectedHistoryItemTypes}
      />

      {/************************************
       * Message Box
       ************************************/}
      <RequirePerms waterServices="UPDATE">
        <Flex
          sx={{ borderTop: '1px solid', borderTopColor: 'tableBorder' }}
          flexGrow={0}
          flexShrink={0}
          p={4}
        >
          <Textarea
            height={34}
            value={comment}
            onBlur={(e) => {
              if (!e.target.value) {
                e.target.style.height = '34px';
              }
            }}
            onFocus={(e) => {
              e.target.style.height = '66px';
            }}
            onKeyPress={async (e) => {
              if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                e.preventDefault();
                if (comment.trim().length > 0) {
                  await createWaterServiceComment({
                    variables: {
                      input: {
                        waterServiceId,
                        comment,
                      },
                    },
                  });
                  setComment('');
                  if (refetch) {
                    await refetch();
                  }
                  scrollDiv.current.scrollTop = 0;
                }
              }
            }}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            placeholder="Enter note"
            sx={{ flexGrow: 1, transition: 'height .3s' }}
          />
        </Flex>
      </RequirePerms>
    </Flex>
  );
};

export default WaterServiceHistory;
